import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../../components/common/Buttons/Button";
import Title from "../../components/common/Title/Title";
import Modal from "../../components/common/Modal";
import SelectFilter from "../../components/common/Inputs/SelectFilter";
import { useDealers } from "../../features/dealers";
import { useDealer } from "../../hooks/useDealer";
import { useDebounce } from "../../utils/useDebounce";
import { useOrders, useSaveOrder } from "../../features/orders";
import { IOrderFilter, OrderInterface } from "../../interfaces/orderInterface";
import { IApiPagination, IPagination } from "../../interfaces/IPagination";
import {
  ColumnInterface,
  ExtraDataInterface,
} from "../../interfaces/customTableInterface";
import CustomTable from "../../components/common/Table/CustomTable";
import orderStatusList, {
  OrderStatusInterface,
  orderStatusOptionList,
} from "../../constants/orderStatus";
import { FormikHelpers, useFormik } from "formik";
import orderSchema from "../../schemas/orderSchema";
import SelectInput, {
  OptionType,
} from "../../components/common/Inputs/SelectInput";
import ProductsForm from "./components/ProductsForm";
import Divider from "../../components/Divider";
import Checkbox from "../../components/common/Inputs/Checkbox";
import { getFullDateFormat } from "../../utils/dateFormater";
import formatToMoney from "../../utils/formatToMoney";
import AddressSelector from "./components/AddressSelector";
import {
  AddressesDataInterface,
  DealerInterface,
} from "../../interfaces/dealerInterface";
import Loading from "../../components/Loading";
import paymentTypes from "../../constants/paymentTypes";
import TextArea from "../../components/common/Inputs/TextArea";
import { MoralTaxRegime } from "../../constants/sistem";
import Tabs from "../../components/common/Tabs/TabContainer";
import Tab from "../../components/common/Tabs/Tab";
import Payments from "./components/Payments";
import CustomInput from "../../components/common/Inputs/CustomInput";
import { paymentMethods } from "../../constants/paymentMethods";
import Pagination from "../../components/common/Pagination";
import { FaSistrix, FaBan } from "react-icons/fa";
import ShippingCostSelector, {
  ShippingCostSelectorProps,
} from "./components/ShippingCostSelector";
import useShippingCosts from "./hooks/useShippingCosts";
import { isTodayGreaterThanMoralTaxesCalcLimitDate } from "../../utils/isTodayGreaterThanMoralTaxesCalcLimitDate";
import { useOrderCalculation } from "./hooks/useOrderCalculation";
import { useProductsOrderStore } from "../../stores/useProductsOrderStore";
import { ButtonIcon } from "../../components/common/Buttons/ButtonIcon";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { useShippingCode } from "./hooks/useSendShippingCode";

const columns: ColumnInterface<OrderInterface>[] = [
  {
    key: "orderNumberFormated",
    label: "No. Orden",
  },
  {
    key: "dealerId",
    label: "Distribuidor",
  },
  {
    key: "status",
    label: "Status",
  },
  {
    key: "total",
    label: "Total",
    isMoney: true,
  },
  {
    key: "totalPayed",
    label: "Saldo",
    isMoney: true,
  },
  {
    key: "requiresInvoice",
    label: "Requiere factura",
    isBoolean: true,
  },
  {
    key: "orderDate",
    label: "Fecha de Orden",
    isDate: true,
  },
];

const formDataInit: OrderInterface = {
  dealerId: "",
  status: 0,
  orderDate: new Date(),
  orderNumber: 0,
  orderNumberFormated: "",
  products: [],
  subTotal: 0,
  discount: 0,
  iva: 0,
  isr: 0,
  total: 0,
  requiresInvoice: false,
  paymentMethod: "",
  paymentType: "",
  payments: [],
  applyPromotion: true,
  applyShippingCost: true,
};

const filterInit: IOrderFilter = {
  orderNumber: "",
  dealerId: "",
  statusId: undefined,
  paymentStatus: "",
};

const paginationInit: IApiPagination = { page: 1, pageSize: 20 };

const paginationDataInit: IPagination = {
  currentPage: 1,
  totalPages: 0,
  pageSize: 20,
  total: 0,
};

const pageSizeOptions: OptionType[] = [
  {
    label: "10",
    value: "10",
  },
  {
    label: "20",
    value: "20",
  },
  {
    label: "50",
    value: "50",
  },
];

const paymentStatusList: OptionType[] = [
  {
    label: "Pendiente de pago",
    value: "1",
  },
  {
    label: "Por saldar",
    value: "2",
  },
  {
    label: "Pagado",
    value: "3",
  },
];

const OrdersPage = () => {
  const setSelectedProducts = useProductsOrderStore(
    (s) => s.setSelectedProducts,
  );
  const [showModal, setShowModal] = useState<boolean>(false);

  const [filter, setFilterState] = useState<IOrderFilter>(filterInit);
  const [pagination, setPaginationState] =
    useState<IApiPagination>(paginationInit);

  const setFilter = useCallback((key: keyof IOrderFilter, value: string) => {
    setFilterState((prev) => ({ ...prev, [key]: value }));
  }, []);
  const clearFilter = useCallback(() => setFilterState(filterInit), []);
  const setPagination = useCallback(
    (key: keyof IApiPagination, value: number) => {
      setPaginationState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const [dealerFilterSearchTerm, setDealerFilterSearchTerm] = useState("");
  const debouncedDealerFilterSearchTerm = useDebounce(
    dealerFilterSearchTerm,
    400,
  );
  const { dealers: dealerFilterOptions } = useDealers({
    filter: { search: debouncedDealerFilterSearchTerm },
    pagination: { page: 1, pageSize: 20, sortBy: "name", sortOrder: "asc" },
  });
  const { data: selectedFilterDealer } = useDealer({ dealerId: filter.dealerId });

  const [dealerFormSearchTerm, setDealerFormSearchTerm] = useState("");
  const debouncedDealerFormSearchTerm = useDebounce(dealerFormSearchTerm, 400);
  const { dealers: dealerFormOptions } = useDealers({
    filter: { search: debouncedDealerFormSearchTerm },
    pagination: { page: 1, pageSize: 20, sortBy: "name", sortOrder: "asc" },
  });

  // Formik init
  const formik = useFormik<OrderInterface>({
    initialValues: formDataInit,
    validationSchema: orderSchema,
    onSubmit: (
      values: OrderInterface,
      formikHelpers: FormikHelpers<OrderInterface>,
    ) => {
      const formCallback = () => {
        formikHelpers.setSubmitting(false); // You can perform additional actions here if needed
        formikHelpers.resetForm();
        handleClose();
      };
      handleSave(values, formCallback);
    },
  });

  const { handleClearOrderCalculation } = useOrderCalculation({
    formik: formik,
  });

  const { shippingCosts, selectedShippingCost, onShippingCostOnChange } =
    useShippingCosts({ formik, modalIsOpen: showModal });

  const { data: selectedDealer } = useDealer({ dealerId: formik.values.dealerId });

  const {
    orders,
    paginationData = paginationDataInit,
    isLoading: loading,
    refetch,
  } = useOrders({ filter, pagination });
  const { handleSave } = useSaveOrder();
  /**
   * This enable the order calc when user update distributor, applying promo flag,
   * special discount, applying shipping cost and updating shipping cost
   */
  const setAllowCalculate = useProductsOrderStore((s) => s.setAllowCalculate);

  const isTaxRegimeMoral = useMemo(() => {
    return (
      selectedDealer &&
      selectedDealer.invoiceData?.taxRegime === MoralTaxRegime &&
      !isTodayGreaterThanMoralTaxesCalcLimitDate()
    );
  }, [selectedDealer]);

  useEffect(() => {
    if (
      paginationData.totalPages !== 0 &&
      paginationData.totalPages < pagination.page
    ) {
      setPagination("page", 1);
    }
  }, [paginationData, pagination.page, setPagination]);

  const { handleSendShippingCode } = useShippingCode({
    dealerId: formik.values.dealerId,
    shippingCode: formik.values.shippingCode,
    shippingCost: formik.values.shippingCost,
  });

  const extraData: ExtraDataInterface<
    OrderStatusInterface | DealerInterface
  >[] = [
    {
      src: orderStatusList,
      dataType: "status",
    },
  ];

  const handleClose = () => {
    setShowModal(false);
    formik.resetForm();
    handleClearOrderCalculation(); // Clear selected products when closing the modal
  };

  const handleEdit = (order: OrderInterface) => {
    setShowModal(true);
    formik.setValues({ ...formik.values, ...order });
    const productsDTO = order.products.map((p) => ({
      productId: p._id,
      quantity: p.quantity,
    }));
    setSelectedProducts(productsDTO);
  };

  const handleDealerOnChange = (dealerId: string) => {
    let specialDiscount = undefined;
    if (dealerId) {
      const dealer = dealerFormOptions.find((x) => x._id === dealerId);
      specialDiscount = dealer?.specialDiscount;
      setAllowCalculate(true);
    }
    formik.setFieldValue("dealerId", dealerId);
    formik.setValues({ ...formik.values, dealerId, specialDiscount });
  };

  const handleNewOnClick = async () => {
    setShowModal(true);
    handleClearOrderCalculation(); // Clear selected products when creating a new order
  };

  const dealerAddresses: AddressesDataInterface[] = useMemo(() => {
    return (
      selectedDealer?.addresses.map((x) => {
        const addressLabel = x.isFiscalAddresses
          ? `(Fiscal) ${x.address}`
          : x.address;
        return {
          ...x,
          address: addressLabel,
        };
      }) ?? []
    );
  }, [selectedDealer]);

  const handleRequiresInvoiceOnChange = () => {
    const nValue = formik.values.requiresInvoice ? false : true;
    const defaultFiscalAddress = dealerAddresses.find(
      (x) => x.isFiscalAddresses,
    );
    formik.setValues(
      {
        ...formik.values,
        requiresInvoice: nValue,
        fiscalAddress: defaultFiscalAddress?._id,
      },
      false,
    );
    formik.setFieldValue("requiresInvoice", nValue);
  };

  const onPageChange = (pageNumber: number) => {
    if (pageNumber !== paginationData.currentPage) {
      setPagination("page", pageNumber);
    }
  };

  const handleFilter = () => {
    refetch();
  };
  const handleClearFilter = () => {
    clearFilter();
  };

  const handleShippingCostOnChange: ShippingCostSelectorProps["onChange"] = (
    value,
  ) => {
    onShippingCostOnChange(value);
    setAllowCalculate(true);
  };

  return (
    <div>
      <Title title="Pedidos" />
      <div className="grid grid-cols-2 lg:grid-cols-5 xl:grid-cols-7 gap-4 items-end">
        <SelectFilter
          label="Distribuidor"
          data={dealerFilterOptions}
          dataKey={"_id"}
          dataLabel={"name"}
          selected={filter.dealerId ? filter.dealerId : ""}
          selectedValue={selectedFilterDealer}
          onSelect={(value) => setFilter("dealerId", value)}
          onSearchTermChange={setDealerFilterSearchTerm}
        />
        <SelectInput
          label="Estatus"
          options={orderStatusOptionList}
          value={filter.statusId ? filter.statusId : ""}
          onChange={(e) => setFilter("statusId", e.target.value)}
        />
        <SelectInput
          label="Estatus de pago"
          options={paymentStatusList}
          value={filter.paymentStatus ? filter.paymentStatus : ""}
          onChange={(e) => setFilter("paymentStatus", e.target.value)}
        />
        <CustomInput
          label="No. Orden"
          type="text"
          value={filter.orderNumber}
          onChange={(e) => setFilter("orderNumber", e.target.value)}
        />
        <div>
          <Button
            size="medium"
            className="mb-2 mr-2"
            onClick={() => handleFilter()}
            color="primary"
          >
            <FaSistrix />
          </Button>
          <Button
            size="medium"
            className="mb-2"
            onClick={() => handleClearFilter()}
            color="secondary"
          >
            <FaBan />
          </Button>
        </div>
      </div>
      {loading ? (
        <Loading />
      ) : (
        <>
          <div className="flex items-end justify-between">
            <div className="w-32">
              <label className="block font-medium mr-2">Ver</label>
              <SelectInput
                options={pageSizeOptions}
                value={pagination.pageSize}
                onChange={(e) => {
                  setPagination("pageSize", parseInt(e.target.value));
                }}
              />
            </div>
            <Button
              size="medium"
              className="mb-2"
              onClick={handleNewOnClick}
              color="primary"
            >
              Nuevo
            </Button>
          </div>
          <CustomTable
            tableId="order-table"
            data={orders}
            dataKey="_id"
            columns={columns}
            onEdit={handleEdit}
            extraData={extraData}
            downloadPDF={true}
            customRender={(key, item) => {
              if (key === "dealerId") {
                return item.dealer?.name;
              }
              if (key === "totalPayed") {
                const totalPayed = item?.totalPayed ?? 0;
                return formatToMoney(item.total - totalPayed);
              }
            }}
          />
          <Pagination
            totalPages={paginationData.totalPages}
            currentPage={paginationData.currentPage}
            onPageChange={onPageChange}
          />
        </>
      )}
      <Modal
        show={showModal}
        close={handleClose}
        submit={formik.handleSubmit}
        size="lg"
      >
        <Tabs>
          <Tab title="Datos de la Orden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectFilter
                label="Distribuidor"
                data={dealerFormOptions}
                dataKey={"_id"}
                dataLabel={"name"}
                selected={formik.values.dealerId}
                selectedValue={selectedDealer}
                onSelect={handleDealerOnChange}
                onSearchTermChange={setDealerFormSearchTerm}
                error={
                  formik.touched.dealerId &&
                  formik.errors.dealerId &&
                  Array.isArray(formik.errors.dealerId)
                    ? formik.errors.dealerId.join(", ")
                    : formik.errors.dealerId
                }
              />
              <SelectInput
                label="Estatus"
                options={orderStatusOptionList}
                {...formik.getFieldProps("status")}
                error={
                  formik.touched.status &&
                  formik.errors.status &&
                  Array.isArray(formik.errors.status)
                    ? formik.errors.status.join(", ")
                    : formik.errors.status
                }
              />
              <AddressSelector
                label="Dirección de envio"
                formKey="shipmentAddress"
                dataSrc={dealerAddresses}
                formik={formik}
              />
            </div>
            {formik.values._id && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <b>No. de Orden</b>
                  <p>{formik.values.orderNumberFormated}</p>
                </div>
                <div>
                  <b>{getFullDateFormat(formik.values.orderDate)}</b>
                </div>
              </div>
            )}
            <Divider />
            <ProductsForm
              formik={formik}
              selectedDealer={selectedDealer}
              isTaxRegimeMoral={isTaxRegimeMoral}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              <div className="md:order-last">
                <div className="grid grid-cols-2">
                  <p className="font-bold">Subtotal</p>
                  <p>{formatToMoney(formik.values.subTotal)}</p>
                </div>
                <div className="grid grid-cols-2">
                  <p className="font-bold">IVA 16%</p>
                  <p>{formatToMoney(formik.values.iva)}</p>
                </div>
                {isTaxRegimeMoral && (
                  <div className="grid grid-cols-2">
                    <p className="font-bold">ISR 1.25%</p>
                    <p>{formatToMoney(formik.values.isr)}</p>
                  </div>
                )}
                <div className="grid grid-cols-2">
                  <p className="font-bold">TOTAL</p>
                  <p>{formatToMoney(formik.values.total)}</p>
                </div>
              </div>
              <div>
                <Checkbox
                  label="Aplicar promoción?"
                  id={"applyPromotion"}
                  name="applyPromotion"
                  onChange={() => {
                    formik.setFieldValue(
                      "applyPromotion",
                      !formik.values.applyPromotion,
                    );
                    setAllowCalculate(true);
                  }}
                  checked={formik.values.applyPromotion}
                />
                <CustomInput
                  label="Descuento Especial"
                  type="number"
                  error={
                    formik.touched.specialDiscount &&
                    formik.errors.specialDiscount &&
                    Array.isArray(formik.errors.specialDiscount)
                      ? formik.errors.specialDiscount.join(", ")
                      : formik.errors.specialDiscount
                  }
                  {...formik.getFieldProps("specialDiscount")}
                  onChange={(e) => {
                    formik.handleChange(e);
                    setAllowCalculate(true);
                  }}
                  min={0}
                />
                <Checkbox
                  label="Aplicar costo de envío?"
                  id={"applyShippingCost"}
                  name="applyShippingCost"
                  onChange={() => {
                    formik.setFieldValue(
                      "applyShippingCost",
                      !formik.values.applyShippingCost,
                    );
                    setAllowCalculate(true);
                  }}
                  checked={formik.values.applyShippingCost}
                />
                <ShippingCostSelector
                  label="Método de envío"
                  formKey="shippingCost"
                  formik={formik}
                  value={selectedShippingCost}
                  onChange={handleShippingCostOnChange}
                  shippingCosts={shippingCosts}
                />
                {selectedShippingCost && (
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <CustomInput
                        label="Código de envio"
                        type="text"
                        {...formik.getFieldProps("shippingCode")}
                        onChange={(e) => {
                          formik.handleChange(e);
                        }}
                      />
                    </div>
                    <div className="mb-2">
                      <ButtonIcon
                        onClick={handleSendShippingCode}
                        color="success"
                        icon={faWhatsapp}
                      />
                    </div>
                  </div>
                )}
                <SelectInput
                  label="Método de pago"
                  options={paymentMethods}
                  {...formik.getFieldProps("paymentMethod")}
                  error={
                    formik.touched.paymentMethod &&
                    formik.errors.paymentMethod &&
                    Array.isArray(formik.errors.paymentMethod)
                      ? formik.errors.paymentMethod.join(", ")
                      : formik.errors.paymentMethod
                  }
                />
                <SelectInput
                  label="Tipo de pago"
                  options={paymentTypes}
                  {...formik.getFieldProps("paymentType")}
                  error={
                    formik.touched.paymentType &&
                    formik.errors.paymentType &&
                    Array.isArray(formik.errors.paymentType)
                      ? formik.errors.paymentType.join(", ")
                      : formik.errors.paymentType
                  }
                />
                <Checkbox
                  label="Requiere factura?"
                  id={"requiresInvoice"}
                  name="requiresInvoice"
                  onChange={handleRequiresInvoiceOnChange}
                  checked={formik.values.requiresInvoice}
                />
                {formik.values.requiresInvoice && (
                  <AddressSelector
                    label="Dirección fiscal"
                    formKey="fiscalAddress"
                    dataSrc={dealerAddresses}
                    formik={formik}
                  />
                )}
              </div>
            </div>
            <TextArea
              label="Notas"
              {...formik.getFieldProps("notes")}
              onChange={(e) => {
                formik.handleChange(e);
              }}
            />
          </Tab>
          <Tab title="Pagos">
            <Payments formik={formik} />
          </Tab>
        </Tabs>
      </Modal>
    </div>
  );
};

export default OrdersPage;
